/**
 * UploadProgressChip — the floating, non-blocking surface for a document upload.
 * Mounted once at app root (inside UploadJobProvider). Replaces the old
 * full-screen upload overlay + the abrupt auto-popping "Knowledge Updated" modal:
 * a small bottom pill shows "N of M" while files upload and while Ellie reads
 * them, tap it to open the knowledge-impact sheet with whatever has accumulated
 * so far, dismiss any time, auto-clears a few seconds after it finishes.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../hooks/useTheme';
import Icon from './Icon';
import { useUploadJob } from '../contexts/UploadJobContext';
import IngestionImpactSheet from './IngestionImpactSheet';

/** How long the "done" pill lingers before it clears itself. */
const DONE_LINGER_MS = 9000;

export default function UploadProgressChip() {
  const { job, impact, dismiss } = useUploadJob();
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const styles = makeStyles(theme);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Auto-clear shortly after the job finishes — but never while the user has the
  // knowledge sheet open reading the results.
  useEffect(() => {
    if (job?.status !== 'done' || sheetOpen) return;
    const t = setTimeout(() => dismiss(), DONE_LINGER_MS);
    return () => clearTimeout(t);
  }, [job?.status, sheetOpen, dismiss]);

  if (!job) return null;

  const who = job.projectName?.trim() ? job.projectName.trim() : 'this plan';
  const done = job.status === 'done';
  const uploading = job.status === 'uploading';
  const hasImpact = !!impact;

  const fraction = uploading
    ? (job.total ? job.uploaded / job.total : 0)
    : (job.total ? (job.processed + job.failed) / job.total : 0);
  const pct = Math.max(0, Math.min(100, Math.round(fraction * 100)));

  let title: string;
  let subtitle: string;
  if (uploading) {
    title = `Adding to ${who}`;
    subtitle = `${job.uploaded} of ${job.total} uploaded`;
  } else if (job.status === 'ingesting') {
    title = `Reading ${job.total === 1 ? 'the file' : `${job.total} files`}`;
    subtitle = `${job.processed} of ${job.total} read`;
  } else {
    title = job.failed > 0 ? `Added ${job.processed} · ${job.failed} failed` : `Added ${job.processed} to ${who}`;
    subtitle = hasImpact ? 'Tap to see what was learned' : 'Ellie finished reading them';
  }

  // Tapping is only meaningful once there's something to show.
  const tappable = hasImpact;

  return (
    <>
      <View pointerEvents="box-none" style={[styles.wrap, { bottom: (insets.bottom || 0) + 16 }]}>
        <View style={[styles.chip, { backgroundColor: theme.colors.card, borderColor: theme.colors.border }]}>
          <View style={styles.row}>
            <Pressable
              style={styles.tapArea}
              onPress={() => tappable && setSheetOpen(true)}
              disabled={!tappable}
            >
              <View style={[styles.iconWrap, { backgroundColor: theme.colors.primary + '18' }]}>
                {done
                  ? <Icon icon={['fad', hasImpact ? 'sparkles' : 'circle-check']} size={16} color={theme.colors.primary} />
                  : <ActivityIndicator size="small" color={theme.colors.primary} />}
              </View>
              <View style={styles.text}>
                <Text style={[styles.title, { color: theme.colors.text }]} numberOfLines={1}>{title}</Text>
                <Text style={[styles.sub, { color: theme.colors.muted }]} numberOfLines={1}>
                  {subtitle}{!done ? ` · ${pct}%` : ''}
                </Text>
              </View>
              {tappable ? <Icon icon={['fas', 'chevron-up']} size={12} color={theme.colors.muted} /> : null}
            </Pressable>

            <TouchableOpacity onPress={dismiss} style={styles.closeBtn} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Icon icon={['fas', 'xmark']} size={14} color={theme.colors.muted} />
            </TouchableOpacity>
          </View>

          <View style={[styles.track, { backgroundColor: theme.colors.border }]}>
            <View style={[styles.fill, { backgroundColor: theme.colors.primary, width: `${done ? 100 : pct}%` }]} />
          </View>
        </View>
      </View>

      <IngestionImpactSheet
        visible={sheetOpen}
        data={impact}
        onClose={() => setSheetOpen(false)}
      />
    </>
  );
}

const makeStyles = (theme: ReturnType<typeof useTheme>) =>
  StyleSheet.create({
    wrap: {
      position: 'absolute',
      right: 16,
      left: 16,
      alignItems: 'flex-end',
      zIndex: 1000,
    },
    chip: {
      width: 300,
      maxWidth: '100%',
      borderRadius: 14,
      borderWidth: StyleSheet.hairlineWidth,
      paddingTop: 12,
      paddingBottom: 10,
      paddingHorizontal: 14,
      shadowColor: '#000',
      shadowOpacity: 0.18,
      shadowRadius: 16,
      shadowOffset: { width: 0, height: 8 },
      elevation: 8,
    },
    row: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    tapArea: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 11 },
    iconWrap: { width: 32, height: 32, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
    text: { flex: 1 },
    title: { fontSize: 14, fontWeight: '600' },
    sub: { fontSize: 12, marginTop: 1 },
    closeBtn: { paddingLeft: 4 },
    track: { height: 5, borderRadius: 999, overflow: 'hidden', marginTop: 10 },
    fill: { height: '100%', borderRadius: 999 },
  });
